import moment from 'moment';

const WEEK_FORMAT = 'DD/MM/YYYY';

/**
 * Formats a number for the payment platform tables, grouping thousands so large rand
 * values stay readable (9780.5 renders as "9,780.50").
 *
 * The locale is pinned to en-US rather than en-ZA on purpose: en-ZA groups with spaces
 * and uses a comma as the decimal separator, which would render R 9 780,50 — the opposite
 * of what was asked for.
 *
 * @param {number|string} v - The value to format.
 * @param {number} [precision=2] - Fixed decimal places.
 * @returns {string} The grouped, fixed-precision value, or the input unchanged when it is
 *   null, undefined, '-', or otherwise not a number.
 */
export const formatDecimal = (v, precision = 2) => {
  if (v === undefined || v === null || v === '-') return v;
  const n = Number.parseFloat(v);
  if (Number.isNaN(n)) return v;
  return n.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

/**
 * Converts the shared `week` state (`{ from, to }` strings in DD/MM/YYYY format)
 * into the `{ start, end }` Date shape expected by the DateRangePicker component.
 *
 * @param {{from: string, to: string}} week - The shared week/range state.
 * @returns {{start: Date, end: Date}} The range as Date objects.
 */
export const toDateObjs = (week) => ({
  start: moment(week.from, WEEK_FORMAT).toDate(),
  end: moment(week.to, WEEK_FORMAT).toDate(),
});

/**
 * Converts a DateRangePicker `{ start, end }` Date range back into the shared
 * `week` state shape (`{ from, to }` strings in DD/MM/YYYY format) used across
 * the payment platform's fetch handlers.
 *
 * @param {{start: Date, end: Date}} range - The confirmed range from DateRangePicker.
 * @returns {{from: string, to: string}} The range as DD/MM/YYYY strings.
 */
export const toStrings = (range) => ({
  from: moment(range.start).format(WEEK_FORMAT),
  to: moment(range.end).format(WEEK_FORMAT),
});

export const formatCurrency = (value) => `R${formatDecimal(Number(value) || 0)}`;

// Legacy (payment_platform_mvp OFF) aggregator feeds. The MVP tabs hide these
// top-of-page Summary breakdowns, but the legacy flow still renders them, so the
// transforms must stay until the flag is removed.
export const transformConsolidatedTotals = (data) => {
    return {
      "Payout": {
        main: data.consolidated_totals.total_payout,
        diff: data.consolidated_totals.payout_diff,
        brackets: data.consolidated_totals.previous_payout
      },
      "Total Corporate Earnings": {
        main: data.consolidated_totals.total_corporate_earnings,
        diff: data.consolidated_totals.corporate_earnings_diff,
        brackets: data.consolidated_totals.previous_corporate_earnings
      },
      "Trip Turnover": {
        main: data.consolidated_totals.total_trips,
        diff: data.consolidated_totals.trips_diff,
        brackets: data.consolidated_totals.previous_trips
      },
    };
  };

export const transformFleetTotals = (data) => {
    const cd = data.fleet_overview.company_data;
    return {
        "Payout": {
        main: Number.parseFloat(cd.total_payout) || 0,
        diff: "-",
        brackets: "-"
        },
        "Access to Site": {
        main: Number.parseFloat(cd.total_access_to_site) || 0,
        diff: "-",
        brackets: "-"
        },
        "Deduction": {
        main: Number.parseFloat(cd.total_deduction) || 0,
        diff: "-",
        brackets: "-"
        },
        // "Running Balance": {
        //   main: data.fleet_overview.company_data.total_balance,
        //   diff: "-",
        //   brackets: "-"
        // },
    };
};

export const transformFleetTrips = (data) => {
    return {
        "Total Trips": { main: data.fleet_overview.total_trips, diff: data.fleet_overview.trip_diff },
        ...Object.fromEntries(
        data.fleet_overview.trip_breakdown.map(([name, count]) => [
            name,
            { main: count, diff: null },
        ])
        ),
    };
};

